/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx, useTheme } from '@emotion/react';
import React, { FC, useMemo, useState, useEffect } from 'react';
import { BnIcon } from '../../../../components/icons_v2';
import { Icons } from '../../../../types';
import { useMiddlewareContext } from '../../../hooks/useMiddlewareContext';
import { ECalendarMode, IDayToDropProps, IEventComponentProps, ISlotsGroup, IOptions } from '../../../types';
import { calculateModalPosition } from '../../../utils';
import { Droppable } from '../../Droppable';
import {
	collisionGroupContainerHorizontal,
	horizontalDayToDropContainer,
	moreItemsButtonHorizontal,
	moreModal,
} from '../../styles';
import { AddNewSlotButtonsContainer } from '../AddNewSlotButtons';
import { DayTimeGridSurface } from '../DayTimeGridSurface';
import { DraggableHorizontal } from './DraggableHorizontal';
import { ResourceNameTypeEnum } from '../../../../types/enums';
import { LoadingDailyContainer } from '../../LoadingDailyContainer';
import { getDraggableId } from '../../../utils/consts';
import { ISlot } from '../../../../organisms/bo_calendar_slot/types';
import { useResourceSlots } from '../../../hooks/useResourceSlots';
import { DEFAULT_NEW_SLOT_HORIZONTAL_HOUR_SIZE } from '../../../utils/timeUtils';

const MAX_COUNT = 4;

export const DayToDropHorizontal: FC<IDayToDropProps> = ({
	id,
	EventComponent,
	onAddNewSlotClick,
	currentDate,
	isLoading,
	activityTimes,
}) => {
	const { slots, updateSlotData } = useResourceSlots({ resourceId: Number(id) });

	const { getSlotGroups, day: daysCount, options } = useMiddlewareContext();
	const colors = useTheme();

	const hourSize: number = options?.hourSize?.horizontal ?? DEFAULT_NEW_SLOT_HORIZONTAL_HOUR_SIZE;

	const slotsGroups = useMemo(() => {
		return getSlotGroups(slots, true, options.date);
	}, [slots, options.date, hourSize, getSlotGroups]);

	return (
		<div data-aid="DayToDropHorizontal" css={horizontalDayToDropContainer(daysCount, colors)}>
			<DayTimeGridSurface
				hourSize={hourSize}
				gridLineStepMinutes={options?.gridLineStepMinutes}
				axis="horizontal"
				slotButtons={
					isLoading ? undefined : (
						<AddNewSlotButtonsContainer
							parentId={id}
							horizontal={true}
							onClick={onAddNewSlotClick}
							activityTimes={activityTimes}
							hourSize={hourSize}
							date={currentDate}
						/>
					)
				}
			>
				{isLoading ? (
					<LoadingDailyContainer />
				) : (
					<Droppable id={id}>
						{slotsGroups.map((group, index) => {
							if (Array.isArray(group.groups)) {
								return (
									<HorizontalSlotGroup
										key={`HorizontalSlotGroup-${index}`}
										group={group}
										index={index}
										EventComponent={EventComponent}
										updateSlotData={updateSlotData}
										id={id}
										currentDate={currentDate}
										options={options}
									/>
								);
							} else if (group.item) {
								return (
									<DraggableSlotContainer
										key={`DraggableSlotHorizontal-${group.item.id}`}
										slot={group.item}
										mode={options.mode}
										id={id}
										currentDate={currentDate}
										EventComponent={EventComponent}
										updateSlotData={updateSlotData}
									/>
								);
							}

							return null;
						})}
					</Droppable>
				)}
			</DayTimeGridSurface>
		</div>
	);
};

type DraggableSlotContainerProps = {
	slot: ISlot;
	id: string;
	currentDate?: string;
	group?: ISlotsGroup;
	withModal?: boolean;
	index?: number;
	EventComponent: FC<IEventComponentProps>;
	mode?: ECalendarMode;
	updateSlotData: (id: number, updatedSlot: Partial<ISlot>, data: any) => void;
};

const DraggableSlotContainer: React.FC<DraggableSlotContainerProps> = ({
	slot,
	id,
	currentDate,
	group,
	withModal,
	index,
	EventComponent,
	mode,
	updateSlotData,
}) => {
	const isEditable: boolean = !slot.isDraft && (String(slot.resourceId) === String(id) || !slot.parentSlotId);

	const draggableId = String(id) === String(slot.resourceId) ? getDraggableId(slot.id, slot.resourceId) : -1;

	const [draggableSlot, setDraggableSlot] = useState(slot);

	useEffect(() => {
		setDraggableSlot(slot);
	}, [slot]);

	return (
		<DraggableHorizontal
			parentId={id}
			state={draggableSlot}
			key={`DraggableHorizontal-${slot.id}`}
			id={draggableId}
			groupTop={group?.top}
			groupCount={withModal ? MAX_COUNT : group?.groups?.length}
			groupIndex={index}
			isEditable={isEditable}
			currentDate={currentDate!}
			setDraggableSlot={setDraggableSlot}
			updateSlotData={updateSlotData}
		>
			<EventComponent
				event={draggableSlot}
				key={`EventComponent-${slot.id}`}
				horizontal
				currentDate={currentDate}
				mode={mode}
			/>
		</DraggableHorizontal>
	);
};

type HorizontalSlotGroupProps = {
	id: string;
	group: ISlotsGroup;
	index: number;
	currentDate: string;
	EventComponent: FC<IEventComponentProps>;
	updateSlotData: (id: number, updatedSlot: Partial<ISlot>, data: any) => void;
	options: IOptions;
};

const HorizontalSlotGroup: React.FC<HorizontalSlotGroupProps> = ({
	id,
	group,
	index,
	currentDate,
	EventComponent,
	updateSlotData,
	options,
}) => {
	const colors = useTheme();

	const [open, setOpen] = useState(false);

	const withModal: boolean = (group.groups?.length || 0) > MAX_COUNT;

	const moreItemsCount = withModal ? group.groups?.slice(3).reduce((acc, events) => acc + events.length, 0) : 0;

	const element = document.querySelector(`#group-${index}`);
	const box = element as HTMLElement;
	const container = box?.parentElement;
	const boxW = container?.parentElement?.parentElement;
	const containerW = boxW?.parentElement;

	return (
		<div id={`group-${index}`} key={index} css={collisionGroupContainerHorizontal(group.top, group.height)}>
			{group.groups?.map((groupItems, index) => {
				return groupItems.map(slot => (
					<DraggableSlotContainer
						key={`DraggableSlotContainer-${slot.id}`}
						slot={slot}
						mode={options.mode}
						id={id}
						group={group}
						currentDate={currentDate}
						withModal={withModal}
						index={index}
						EventComponent={EventComponent}
						updateSlotData={updateSlotData}
					/>
				));
			})}
			{withModal && (
				<button
					data-aid="button-DayToDropHorizontal-more"
					css={moreItemsButtonHorizontal(colors)}
					onClick={() => setOpen(!open)}
				>
					<div>+{`${moreItemsCount} more`}</div>
				</button>
			)}
			{withModal && open && (
				<div css={moreModal(colors, calculateModalPosition(boxW, containerW, box, container))}>
					<div className="more-modal-header">
						<div onClick={() => setOpen(!open)}>
							<BnIcon icon={Icons.close} />
						</div>
					</div>
					<div className="events-list">
						{group.items.map(slot => (
							<div key={slot.id} css={{ margin: 2, height: '50px' }}>
								<EventComponent
									event={slot}
									key={`EventComponent-${slot.id}`}
									horizontal
									currentDate={options.date}
									mode={options.mode}
								/>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
};
